if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // BIÉDE product mobile loop fix v2026-09-06-1
      connectedCallback() {
        if (!this.hasAttribute('data-mobile-loop') || !this.closest('product-info')) return;

        customElements.whenDefined('slider-component').then(() => {
          if (!this.isConnected || this.stopMobileLoop) return;
          const viewer = this.elements.viewer;
          const slider = viewer?.slider;
          if (!slider || !viewer.prevButton || !viewer.nextButton) return;

          const controller = new AbortController();
          const options = { signal: controller.signal };
          const originalLooping = viewer.enableSliderLooping;
          let gesture = null;
          let suppressClickUntil = 0;
          const canLoop = () =>
            !this.mql.matches &&
            viewer.sliderItemsToShow?.length > 1 &&
            slider.scrollWidth > slider.clientWidth + 2;
          const edge = () => {
            if (slider.scrollLeft <= 2) return 'first';
            if (slider.scrollLeft >= slider.scrollWidth - slider.clientWidth - 2) return 'last';
            return null;
          };
          const wrap = (from) => {
            // Jump directly across the boundary instead of animating past every image.
            slider.scrollTo({
              left: from === 'first' ? slider.scrollWidth - slider.clientWidth : 0,
              behavior: 'instant',
            });
          };
          const updateControls = () => {
            viewer.enableSliderLooping = canLoop() || originalLooping;
            if (viewer.sliderItemsToShow?.length > 1) viewer.update();
            if (canLoop()) {
              viewer.prevButton.disabled = false;
              viewer.nextButton.disabled = false;
            }
          };

          slider.addEventListener('touchstart', (event) => {
            gesture = null;
            suppressClickUntil = 0;
            if (!canLoop() || event.touches.length !== 1) return;
            if (!event.target.closest('.product__modal-opener--image')) return;
            const from = edge();
            if (!from) return;
            const touch = event.touches[0];
            gesture = { from, id: touch.identifier, x: touch.clientX, y: touch.clientY, dx: 0, locked: false };
          }, { ...options, passive: true });

          slider.addEventListener('touchmove', (event) => {
            if (!gesture) return;
            if (!canLoop() || event.touches.length !== 1) {
              gesture = null;
              return;
            }
            const touch = event.touches[0];
            if (touch.identifier !== gesture.id) {
              gesture = null;
              return;
            }
            const dx = touch.clientX - gesture.x;
            const dy = touch.clientY - gesture.y;
            if (!gesture.locked) {
              if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
              const outward = gesture.from === 'first' ? dx > 0 : dx < 0;
              if (!outward || Math.abs(dx) <= Math.abs(dy) * 1.25) {
                gesture = null;
                return;
              }
              gesture.locked = true;
            }
            if (!event.cancelable) {
              gesture = null;
              return;
            }
            event.preventDefault();
            gesture.dx = dx;
            suppressClickUntil = performance.now() + 700;
          }, { ...options, passive: false });

          slider.addEventListener('touchend', (event) => {
            const completed = gesture;
            gesture = null;
            if (!completed?.locked || !canLoop()) return;
            const touch = Array.from(event.changedTouches).find((item) => item.identifier === completed.id);
            const dx = touch ? touch.clientX - completed.x : completed.dx;
            const outwardDistance = completed.from === 'first' ? dx : -dx;
            if (outwardDistance >= 40) wrap(completed.from);
          }, { ...options, passive: true });

          slider.addEventListener('touchcancel', () => { gesture = null; }, options);
          slider.addEventListener('click', (event) => {
            if (event.detail === 0 || performance.now() >= suppressClickUntil) return;
            suppressClickUntil = 0;
            event.preventDefault();
            event.stopImmediatePropagation();
          }, { ...options, capture: true });

          viewer.addEventListener('click', (event) => {
            if (!canLoop()) return;
            const button = event.target.closest('button');
            const from = edge();
            if ((button === viewer.prevButton && from === 'first') ||
                (button === viewer.nextButton && from === 'last')) {
              event.preventDefault();
              event.stopImmediatePropagation();
              wrap(from);
            }
          }, { ...options, capture: true });

          const observer = new ResizeObserver(updateControls);
          observer.observe(slider);
          this.mql.addEventListener('change', updateControls, options);
          updateControls();
          this.stopMobileLoop = () => {
            controller.abort();
            observer.disconnect();
            viewer.enableSliderLooping = originalLooping;
            gesture = null;
          };
        });
      }

      disconnectedCallback() {
        this.stopMobileLoop?.();
        this.stopMobileLoop = null;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
